import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, User, LogOut, Home, Trophy, Users,
  ArrowLeft, RefreshCw, X, Package, Truck, CheckCircle, Clock, DollarSign
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function AdminCustomers() {
  const { logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  
  const [topSpenders, setTopSpenders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [displayLimit, setDisplayLimit] = useState('10'); // '10', '25', '50', 'all'
  
  // Customer detail modal state
  const [customerModal, setCustomerModal] = useState({
    open: false,
    customer: null,
    user: null,
    orders: [],
    stats: null,
    loading: false
  });

  const fetchTopSpenders = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/admin/analytics/top-spenders?limit=10000`, {
        headers: getAuthHeader()
      });
      setTopSpenders(response.data);
    } catch (error) {
      console.error('Failed to fetch top spenders:', error);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTopSpenders();
  }, []);

  // Fetch customer orders when clicking on a customer
  const handleViewCustomer = async (customer) => {
    setCustomerModal({
      open: true,
      customer: customer,
      user: null,
      orders: [],
      stats: null,
      loading: true
    });

    try {
      // Find user by email to get their orders
      const usersRes = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeader()
      });
      
      const user = usersRes.data.find(u => 
        u.email?.toLowerCase() === customer.email?.toLowerCase()
      );
      
      if (user) {
        const ordersRes = await axios.get(`${API}/admin/users/${user.id}/orders`, {
          headers: getAuthHeader()
        });
        
        setCustomerModal(prev => ({
          ...prev,
          user: ordersRes.data.user || user,
          orders: ordersRes.data.orders || [],
          stats: ordersRes.data.stats || null,
          loading: false
        }));
      } else {
        // Fallback: search orders by email
        const ordersRes = await axios.get(`${API}/admin/orders`, {
          headers: getAuthHeader()
        });
        
        const customerOrders = (ordersRes.data || []).filter(o => 
          o.email?.toLowerCase() === customer.email?.toLowerCase()
        );
        
        // Calculate stats manually from orders
        const pending = customerOrders.filter(o => o.status === 'pending').length;
        const pendingTotal = customerOrders.filter(o => o.status === 'pending').reduce((sum, o) => sum + (o.total || 0), 0);
        const pendingShipment = customerOrders.filter(o => ['pending_fulfillment', 'pending_shipment'].includes(o.status)).length;
        const shipped = customerOrders.filter(o => ['shipped', 'out_for_delivery'].includes(o.status)).length;
        const delivered = customerOrders.filter(o => o.status === 'delivered').length;
        const confirmedStatuses = ['pending_fulfillment', 'pending_shipment', 'shipped', 'out_for_delivery', 'delivered'];
        const totalSpent = customerOrders.filter(o => confirmedStatuses.includes(o.status)).reduce((sum, o) => sum + (o.total || 0), 0);
        
        setCustomerModal(prev => ({
          ...prev,
          orders: customerOrders,
          stats: {
            pending,
            pending_total: pendingTotal,
            pending_shipment: pendingShipment,
            shipped,
            delivered,
            total_spent: totalSpent,
            total_orders: customerOrders.length
          },
          loading: false
        }));
      }
    } catch (error) {
      console.error('Failed to fetch customer orders:', error);
      setCustomerModal(prev => ({
        ...prev,
        orders: [],
        stats: null,
        loading: false
      }));
    }
  };

  // Get displayed customers based on limit
  const allCustomers = topSpenders?.top_spenders || [];
  const totalCustomers = allCustomers.length;
  
  const displayedCustomers = displayLimit === 'all' 
    ? allCustomers 
    : allCustomers.slice(0, parseInt(displayLimit));
  
  // Calculate dynamic stats for displayed customers
  const displayedStats = {
    count: displayedCustomers.length,
    revenue: displayedCustomers.reduce((sum, c) => sum + (c.total_spent || 0), 0),
    orders: displayedCustomers.reduce((sum, c) => sum + (c.order_count || 0), 0),
    // Average order value = total revenue / total orders (not per customer)
    avgOrderValue: displayedCustomers.reduce((sum, c) => sum + (c.order_count || 0), 0) > 0
      ? displayedCustomers.reduce((sum, c) => sum + (c.total_spent || 0), 0) / displayedCustomers.reduce((sum, c) => sum + (c.order_count || 0), 0)
      : 0,
    contribution: topSpenders?.summary?.total_revenue > 0
      ? ((displayedCustomers.reduce((sum, c) => sum + (c.total_spent || 0), 0) / topSpenders.summary.total_revenue) * 100).toFixed(1)
      : 0
  };

  return (
    <div className="min-h-screen bg-black" data-testid="admin-customers-page">
      {/* Header */}
      <header className="border-b border-zinc-800 sticky top-0 bg-black/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-6 w-auto" />
          </Link>
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="text-zinc-400 hover:text-white">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="text-zinc-400 hover:text-white relative">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-tiffany-500 text-black text-xs w-4 h-4 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="text-zinc-400 hover:text-white">
              <User className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="text-zinc-400 hover:text-white">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Back Button & Title - Improved mobile layout */}
        <div className="flex items-center justify-between mb-6 gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Link to="/admin" className="text-zinc-500 hover:text-white flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-mono text-base sm:text-xl text-white flex items-center gap-2 min-w-0">
              <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 flex-shrink-0" />
              <span className="truncate">TOP CUSTOMERS</span>
            </h1>
          </div>
          <Button
            onClick={fetchTopSpenders}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-400 hover:text-white flex-shrink-0 h-8 w-8 sm:h-9 sm:w-auto sm:px-3"
            data-testid="refresh-customers-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline ml-2">REFRESH</span>
          </Button>
        </div>

        {/* Limit Selector & Summary Stats */}
        {topSpenders?.summary && (
          <div className="mb-6">
            {/* Limit Selector */}
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-xs text-zinc-500">SHOWING TOP</p>
              <Select value={displayLimit} onValueChange={setDisplayLimit}>
                <SelectTrigger className="w-24 h-8 bg-zinc-900 border-zinc-700 text-white font-mono text-xs rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none">
                  <SelectItem value="10" className="font-mono text-xs text-white">10</SelectItem>
                  <SelectItem value="25" className="font-mono text-xs text-white">25</SelectItem>
                  <SelectItem value="50" className="font-mono text-xs text-white">50</SelectItem>
                  <SelectItem value="all" className="font-mono text-xs text-white">ALL ({totalCustomers})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Dynamic Stats for Displayed Customers */}
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-zinc-800 p-3 sm:p-4 text-center">
                <p className="font-mono text-xl sm:text-2xl text-white">{displayedStats.count}</p>
                <p className="font-mono text-[9px] sm:text-[10px] text-zinc-500">
                  {displayLimit === 'all' ? 'ALL' : `TOP ${displayLimit}`} CUSTOMERS
                </p>
              </div>
              <div className="border border-zinc-800 p-3 sm:p-4 text-center">
                <p className="font-mono text-xl sm:text-2xl text-green-500">${displayedStats.revenue?.toLocaleString()}</p>
                <p className="font-mono text-[9px] sm:text-[10px] text-zinc-500">
                  {displayLimit === 'all' ? 'TOTAL' : `TOP ${displayLimit}`} REVENUE
                </p>
              </div>
              <div className="border border-zinc-800 p-3 sm:p-4 text-center">
                <p className="font-mono text-xl sm:text-2xl text-yellow-500">{displayedStats.contribution}%</p>
                <p className="font-mono text-[9px] sm:text-[10px] text-zinc-500">OF TOTAL REVENUE</p>
              </div>
              <div className="border border-zinc-800 p-3 sm:p-4 text-center">
                <p className="font-mono text-xl sm:text-2xl text-tiffany-500">${Math.round(displayedStats.avgOrderValue).toLocaleString()}</p>
                <p className="font-mono text-[9px] sm:text-[10px] text-zinc-500">AVG ORDER</p>
              </div>
            </div>
            
            {/* Overall stats - small text */}
            <div className="mt-3 pt-3 border-t border-zinc-800 flex justify-between text-zinc-600 font-mono text-[10px]">
              <span>ALL {totalCustomers} CUSTOMERS: ${topSpenders.summary.total_revenue?.toLocaleString()}</span>
              <span>{displayedStats.orders} ORDERS</span>
            </div>
          </div>
        )}

        {/* Customer List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="font-mono text-zinc-500">Loading customers...</p>
          </div>
        ) : displayedCustomers.length > 0 ? (
          <div className="space-y-2">
            {displayedCustomers.map((spender, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-3 p-3 sm:p-4 border cursor-pointer transition-all hover:border-tiffany-500/50 ${
                  idx === 0 ? 'border-yellow-500/50 bg-yellow-500/5' :
                  idx === 1 ? 'border-zinc-400/50 bg-zinc-400/5' :
                  idx === 2 ? 'border-amber-600/50 bg-amber-600/5' :
                  'border-zinc-800 hover:bg-zinc-900/50'
                }`}
                onClick={() => handleViewCustomer(spender)}
                data-testid={`customer-row-${idx}`}
              >
                {/* Rank Badge */}
                <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center font-mono text-xs sm:text-sm font-bold flex-shrink-0 ${
                  idx === 0 ? 'bg-yellow-500 text-black' :
                  idx === 1 ? 'bg-zinc-400 text-black' :
                  idx === 2 ? 'bg-amber-600 text-black' :
                  'bg-zinc-800 text-zinc-400'
                }`}>
                  {idx + 1}
                </div>
                
                {/* Customer Info */}
                <div className="flex-1 min-w-0">
                  <span 
                    className="font-mono text-xs sm:text-sm text-white uppercase hover:text-tiffany-500 transition-colors truncate block cursor-pointer"
                  >
                    {spender.name}
                  </span>
                  <p className="font-mono text-[9px] sm:text-[10px] text-zinc-500 truncate">
                    {spender.email}
                  </p>
                </div>
                
                {/* Stats */}
                <div className="text-right flex-shrink-0">
                  <p className="font-mono text-sm sm:text-lg text-green-500 font-bold">
                    ${spender.total_spent.toLocaleString()}
                  </p>
                  <p className="font-mono text-[9px] sm:text-[10px] text-zinc-500">
                    {spender.order_count} orders
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 border border-zinc-800">
            <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="font-mono text-zinc-500">No customer data yet</p>
          </div>
        )}
      </main>

      {/* Customer Orders Modal */}
      <Dialog open={customerModal.open} onOpenChange={(open) => setCustomerModal(prev => ({ ...prev, open }))}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="customer-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-mono text-white flex items-center gap-2">
              <User className="w-5 h-5 text-tiffany-500" />
              {customerModal.customer?.name?.toUpperCase()}
            </DialogTitle>
            <p className="font-mono text-sm text-zinc-400">
              {customerModal.customer?.email}
            </p>
          </DialogHeader>
          
          {customerModal.loading ? (
            <div className="py-8 text-center">
              <div className="text-tiffany-500 font-mono">LOADING...</div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* User Info - Address & Member Since */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-zinc-800/50 border border-zinc-700">
                <div>
                  <p className="font-mono text-xs text-zinc-500">ADDRESS</p>
                  <p className="font-mono text-sm text-white" data-testid="customer-address">
                    {customerModal.user?.address || 'N/A'}
                    {customerModal.user?.apt_unit ? `, ${customerModal.user.apt_unit}` : ''}
                  </p>
                  {(customerModal.user?.city || customerModal.user?.state || customerModal.user?.zip_code) && (
                    <p className="font-mono text-sm text-zinc-400">
                      {customerModal.user?.city}{customerModal.user?.city && customerModal.user?.state ? ', ' : ''}{customerModal.user?.state} {customerModal.user?.zip_code}
                    </p>
                  )}
                </div>
                <div>
                  <p className="font-mono text-xs text-zinc-500">MEMBER SINCE</p>
                  <p className="font-mono text-sm text-white">
                    {customerModal.user?.created_at ? new Date(customerModal.user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Order Stats */}
              {customerModal.stats && (
                <div>
                  <h3 className="font-sans text-lg text-white mb-3">ORDER STATS</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-yellow-500" />
                        <span className="font-mono text-xs text-yellow-500">PENDING</span>
                      </div>
                      <p className="font-mono text-xl text-white">{customerModal.stats.pending || 0}</p>
                      <p className="font-mono text-xs text-zinc-500">${customerModal.stats.pending_total?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-4 h-4 text-blue-500" />
                        <span className="font-mono text-xs text-blue-500">TO SHIP</span>
                      </div>
                      <p className="font-mono text-xl text-white">{customerModal.stats.pending_shipment || 0}</p>
                    </div>
                    <div className="p-3 bg-purple-500/10 border border-purple-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Truck className="w-4 h-4 text-purple-500" />
                        <span className="font-mono text-xs text-purple-500">SHIPPED</span>
                      </div>
                      <p className="font-mono text-xl text-white">{customerModal.stats.shipped || 0}</p>
                    </div>
                    <div className="p-3 bg-green-500/10 border border-green-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="font-mono text-xs text-green-500">DELIVERED</span>
                      </div>
                      <p className="font-mono text-xl text-white">{customerModal.stats.delivered || 0}</p>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-tiffany-500/10 border border-tiffany-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-tiffany-500" />
                        <span className="font-mono text-sm text-tiffany-500">TOTAL SPENT (Confirmed)</span>
                      </div>
                      <span className="font-mono text-xl text-white">${customerModal.stats.total_spent?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Orders List */}
              <div>
                <h3 className="font-sans text-lg text-white mb-3">
                  ORDER HISTORY ({customerModal.stats?.total_orders || customerModal.orders.length || 0})
                </h3>
                {customerModal.orders.length === 0 ? (
                  <p className="font-mono text-sm text-zinc-500 text-center py-4">No orders yet</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {customerModal.orders.map((order, idx) => (
                      <div key={idx} className="p-3 bg-zinc-800 border border-zinc-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-sm text-white">
                            {order.order_id || `#${order.id?.slice(-8).toUpperCase()}`}
                          </span>
                          <div className={`flex items-center gap-1 ${
                            order.status === 'delivered' ? 'text-green-500' :
                            order.status === 'shipped' ? 'text-blue-500' :
                            order.status === 'out_for_delivery' ? 'text-orange-500' :
                            order.status === 'pending' ? 'text-yellow-500' :
                            order.status === 'pending_shipment' || order.status === 'pending_fulfillment' ? 'text-tiffany-500' :
                            'text-zinc-400'
                          }`}>
                            {order.status === 'out_for_delivery' && <Truck className="w-3 h-3" />}
                            {order.status === 'delivered' && <CheckCircle className="w-3 h-3" />}
                            {order.status === 'shipped' && <Package className="w-3 h-3" />}
                            {order.status === 'pending' && <Clock className="w-3 h-3" />}
                            <span className="font-mono text-xs uppercase">{order.status?.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-mono text-zinc-500">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString() : ''}
                          </span>
                          <span className="font-mono text-white">${order.total?.toFixed(2)}</span>
                        </div>
                        <div className="mt-2 text-xs text-zinc-500">
                          {order.items?.map((item, i) => (
                            <span key={i} className="mr-2">{item.name} x{item.quantity}</span>
                          ))}
                        </div>
                        {order.tracking_number && (
                          <p className="font-mono text-xs text-tiffany-500 mt-1">
                            Tracking: {order.tracking_number}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
